import type { ReactNode } from 'react';
import { useMemo } from 'react';

import { BodyMicro } from '#/components/Text/BodyMicro';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { SearchAlgoliaEventHit } from '#/modules/search/algolia/types/SearchAlgoliaEventHit';
import { SearchHitImage } from '#/modules/search/components/Hit/Image/SearchHitImage';
import { SearchHitFloatingSaveButton } from '#/modules/search/components/Hit/SaveButton/SearchHitFloatingSaveButton';
import {
  SearchHitCard,
  SearchHitHolder,
  SearchHitHoverEffect,
  SearchHitImageHolder,
} from '#/modules/search/components/Hit/SearchHit.styled';
import { SearchHitInteractiveContentWrapper } from '#/modules/search/components/Hit/SearchHitInteractiveContentWrapper';
import type { SearchContextHeaderLocationState } from '#/modules/search/containers/SearchContextHeader/SearchContextHeaderLocationState';
import { getSearchHitElementId } from '#/modules/search/utils/getSearchHitElementId';
import type { SearchSavedListingsStore } from '#/modules/search/zustand-stores/searchSavedListingsStore';
import type { DatetimeString } from '#/types/TypedDateString';
import { getWorkLocationString } from '#/utils/address/getWorkLocationString';
import { timeago } from '#/utils/string/datetime/timeago';

import { SearchEventHitInfo } from './SearchEventHitInfo';

type Props = {
  hit: SearchAlgoliaEventHit;
  href: string;
  trackClick: (() => void) | null;
  showLabel: boolean;
  searchSavedListingsStore: SearchSavedListingsStore | null;
  highlighted: boolean;
  application: {
    status: 'ACTIVE' | 'INACTIVE';
    created: DatetimeString;
  } | null;
  now: DatetimeString;
};

export function SearchEventHit({
  hit,
  href,
  trackClick,
  showLabel,
  searchSavedListingsStore,
  highlighted,
  application,
  now,
}: Props) {
  const { i18n, locale } = useI18n();

  const {
    name,
    city,
    published,
    locationType,
    remoteZone,
    image,
    startDate,
    startTime,
    endDate,
    endTime,
    timezone,
    isIdealistDay,
    state,
    country,
    source,
    logo,
    orgName,
    orgType,
  } = hit;

  const location = useMemo(
    () =>
      getWorkLocationString({
        i18n,
        remoteOptions: {
          remoteZone,
          remoteCountry: country,
          remoteState: state,
        },
        address: {
          city,
          country,
          stateCode: state,
        },
      }),
    [i18n, remoteZone, city, country, state],
  );

  const listingStats = useMemo(() => {
    const stats: ReactNode[] = [];

    if (published) {
      stats.push(
        <BodyMicro style={{ lineHeight: 1 }}>
          {i18n('Posted %(postedAt)s', {
            postedAt: timeago(new Date(published * 1000).toISOString(), {
              locale,
              now,
            }),
          })}
        </BodyMicro>,
      );
    }

    return stats;
  }, [i18n, locale, published, now]);

  return (
    <SearchHitHolder
      id={getSearchHitElementId(hit.objectID)}
      data-qa-id="search-result"
    >
      <SearchHitCard>
        <SearchHitInteractiveContentWrapper
          highlighted={Boolean(highlighted)}
          listingLinkData={{
            type: 'link-with-state',
            to: href,
            state: {
              showSearchContext: true,
            } satisfies SearchContextHeaderLocationState,
          }}
          onClick={() => trackClick?.()}
        >
          <SearchHitHoverEffect />

          <SearchHitImageHolder $hiddenOnMobile={!logo}>
            <SearchHitImage
              isImported={false}
              imageUrl={null}
              image={image}
              source={source}
              name={name}
              logo={logo ?? null}
            />
          </SearchHitImageHolder>

          <SearchEventHitInfo
            application={application ?? null}
            startDate={startDate}
            startTime={startTime}
            endDate={endDate}
            endTime={endTime}
            timezone={timezone}
            listingStats={listingStats}
            location={location}
            locationType={locationType}
            name={name}
            orgName={orgName ?? null}
            orgType={orgType}
            showLabel={showLabel}
            isIdealistDay={isIdealistDay}
          />
        </SearchHitInteractiveContentWrapper>

        {searchSavedListingsStore && (
          <SearchHitFloatingSaveButton
            hit={hit}
            searchSavedListingsStore={searchSavedListingsStore}
          />
        )}
      </SearchHitCard>
    </SearchHitHolder>
  );
}
